import React, { useEffect, useState } from "react";

import { Button, Card } from "@cofano/cck";

import { Alert, Box, CircularProgress, Tooltip, Typography } from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileContract, faShip } from "@fortawesome/pro-duotone-svg-icons";

import { DetailsBlock, Height } from "../Blocks";

import { Contract, PurchaseCharter, SalesCharter } from "types/index";

import { ContractLink, RelationLink } from "utils/LinkUtils";
import ContractCreateEditDialog from "view/contractsNew/components/dialogs/ContractCreateEditDialog";
import { usePermission } from "utils";
import EditIcon from "@mui/icons-material/Edit";
import AgreementDetails from "view/agreements/AgreementDetails";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
	checkCopiedAgreementsChanged_query,
	copyAgreements_mutation,
	updateAgreementsHaveChanged_mutation,
} from "view/contractsNew/utils/ContractQueries";
import { useConfirm } from "components/hooks/useConfirm";
import { intl } from "LocaleWrapper";
import Grid from "@mui/material/Grid2";
import { AgreementTypeEnum } from "__generated__/graphql";
import { AssignmentInd, ContentCopy } from "@mui/icons-material";
import { CharterSubtype, CharterType, HistoryEntity } from "__generated__/enums";
import { PurchaseCharterFromQuery } from "view/charters/PurchaseCharterDetails";
import { CharterContractFragment } from "__generated__/view/contractsNew/graphql/contract.fragments.generated";
import HistoryIcon from "@mui/icons-material/History";
import EntityHistoryDialog from "components/entityHistory/components/dialogs/EntityHistoryDialog";

type ContractAgreementsBlockProps = {
	contract: Contract | CharterContractFragment;
	color: string;
	charter: SalesCharter | PurchaseCharter | PurchaseCharterFromQuery;
	refetch: () => void;
	otherContractId?: number;
	voyageId?: number;
};

const ContractAgreementsBlock = ({ contract, color, charter, refetch, otherContractId, voyageId }: ContractAgreementsBlockProps) => {

	const { isReadOnly } = usePermission();
	const confirm = useConfirm();

	const [ dialog, setDialog ] = useState<{ open: boolean, contractId?: number }>({ open: false, contractId: undefined });
	const [ copyAgreements, { loading: copyAgreementsLoading } ] = useMutation(copyAgreements_mutation);
	const [ updateAgreementsHaveChanged, { loading: updateAgreementsChangedLoading } ] = useMutation(updateAgreementsHaveChanged_mutation);
	const [ historyDialogOpen, openHistoryDialog ] = useState<boolean>(false);

	const canCopyAgreements = otherContractId != null && !isReadOnly;

	const [ checkCopiedAgreementsChanged, { loading: checkCopiedAgreementsLoading } ] = useLazyQuery(checkCopiedAgreementsChanged_query);

	const contentLoading = copyAgreementsLoading || updateAgreementsChangedLoading || checkCopiedAgreementsLoading;

	const getCardInformation = () => {
		if (contract.charterType === CharterType.Purchase) {
			return {
				icon: <FontAwesomeIcon icon={faShip} style={{ color: color, height: 22, width: 22 }} />,
				relation: contract.creditor ?? undefined,
			};
		} else if (contract.charterType === CharterType.Sales) {
			return {
				icon: <AssignmentInd sx={{ color: color }} />,
				relation: contract.debtor ?? undefined,
			};
		}
		return null;
	};

	const getCopyFromTitle = () => {
		if (contract.charterType === CharterType.Sales) {
			return intl.formatMessage({ id: "COPY_AGREEMENTS_FROM_PURCHASE" });
		} else if (contract.charterType === CharterType.Purchase) {
			return intl.formatMessage({ id: "COPY_AGREEMENTS_FROM_SALES" });
		}
	};

	const onCopyAgreements =  () => {
		const shipAgreement = contract.agreements?.find(item => item?.type === AgreementTypeEnum.Ship);
		if (shipAgreement != null) {
			confirm({
				type: "WARNING",
				title: intl.formatMessage({ id: "CONTRACT_SHIP_AGREEMENT_COPY_WARNING_TITLE" }),
				content: <Typography color={"textPrimary"}>
					{intl.formatMessage({ id: "CONTRACT_SHIP_AGREEMENT_COPY_WARNING_CONTENT" })}
				</Typography>,
				confirmText: intl.formatMessage({ id: "CONTRACT_AGREEMENTS_COPY" }),
			}).then(() => copy());
		} else {
			copy();
		}
	};

	const copy = () => {
		if (!otherContractId || !contract.id) {
			return;
		}
		copyAgreements({
			variables: {
				from: otherContractId,
				to: contract.id,
			},
		}).then(() => {
			refetch();
		});
	};

	useEffect(() => {
		if (!contract.agreementsHaveChanged || otherContractId == null) {
			return;
		}

		checkCopiedAgreementsChanged({
			variables: {
				from: otherContractId,
				to: contract.id,
			},
		}).then(({ data }) => {
			if (data?.copiedAgreementsChanged) {
				confirm({
					type: "WARNING",
					title: intl.formatMessage({ id: "CONTRACT_AGREEMENTS_CHANGED_TITLE" }),
					content: <Typography color="textPrimary">
						{intl.formatMessage({ id: "CONTRACT_AGREEMENTS_CHANGED_CONTENT" })}
					</Typography>,
					confirmText: intl.formatMessage({ id: "CONTRACT_AGREEMENTS_COPY" }),
					secondConfirmButton: (onClose) => (
						<Button
							onClick={() => {
								onClose();
								updateAgreementsHaveChanged({
									variables: {
										contractId: contract.id,
										agreementsHaveChanged: false,
									},
								}).then(() => refetch());
							}}
							size="small">
							{intl.formatMessage({ id: "NO" })}
						</Button>
					),
				}).then(() => {
					onCopyAgreements();
				});
			}
		});

	}, [ contract ]);

	const cardInformation = getCardInformation();

	return (
		<React.Fragment>
			<DetailsBlock height={Height.Auto}>
				<Card
					actions={[
						{
							icon: <HistoryIcon fontSize="small" />,
							tooltip: intl.formatMessage({ id: "HISTORY" }),
							onClick: () => openHistoryDialog(true),
						},
						{
							hidden: !canCopyAgreements,
							render: () => <Tooltip title={getCopyFromTitle()}>
								<span>
									<Button
										onClick={onCopyAgreements}
										disabled={!contract.isEditable || (contract.charterType === CharterType.Purchase && contract.charterSubtype === CharterSubtype.Time)}
										variant="text"
										size="small"
									>
										<ContentCopy fontSize="small" />
									</Button>
								</span>
							</Tooltip>,
						},
						{
							hidden: isReadOnly,
							icon: <EditIcon fontSize="small" />,
							onClick: () => setDialog({ open: true, contractId: contract.id }),
							disabled: !contract.isEditable,
						},
					]}
					color={color}
					icon={cardInformation?.icon ?? <FontAwesomeIcon icon={faFileContract} style={{ color: color, height: 22, width: 22 }} />}
					title={<ContractLink contract={contract} />}
					subtitle={cardInformation?.relation ? <RelationLink relation={cardInformation.relation} /> : undefined}
					disablePadding>
					{contentLoading ? (
						<Grid size={12}>
							<Box sx={{ padding: "16px", display: "flex", justifyContent: "center" }}>
								<CircularProgress size={16} />
							</Box>
						</Grid>
					) : (
						<Box sx={{ borderTop: 1, borderColor: "divider", display: "flex", height: "100%" }}>
							<div style={{ backgroundColor: "rgb(240, 240, 240)", overflowX: "hidden", overflowY: "auto", width: "100%" }}>
								<div className="contract-details">
									{"hasApplicableContract" in charter && !charter.hasApplicableContract && (
										<Alert severity="warning" sx={{ m: 2, mb: 0 }}>{intl.formatMessage({ id: "INCOMPATIBLE_CONTRACT_WARNING" })}</Alert>
									)}

									<AgreementDetails
										contractId={contract.id}
										currency={contract.currency || { isoCode: "EUR", symbol: "€" }}
										charterType={contract.charterType}
										charterSubtype={contract.charterSubtype}
										otherContractId={otherContractId}
										showCreateTemplateButton={false}
										voyageId={voyageId}
									/>
								</div>
							</div>
						</Box>
					)}
				</Card>

				<ContractCreateEditDialog
					open={dialog.open}
					onClose={() => setDialog({ open: false, contractId: undefined })}
					charter={charter}
					contractId={dialog.contractId}
					refetch={() => refetch()}
					hideContractDetails={true}
				/>

				<EntityHistoryDialog
					title={intl.formatMessage({ id: "HISTORY" })}
					open={historyDialogOpen}
					onClose={() => openHistoryDialog(false)}
					entity={HistoryEntity.Contract}
					entityId={contract.id}
				/>
			</DetailsBlock>
		</React.Fragment>
	);
};

export default ContractAgreementsBlock;
